import React, { useContext, useMemo, useState } from 'react';
import { useSearchTerm } from '../../../hooks/useSearchTerm';
import { useSyncPageParams } from '../../../hooks/useSyncPageParams';
import { useUserSorts } from '../../../hooks/useUserSorts';
import { EventAvailableColumns, EventSortParam, useGetAllEvents, UseGetAllEventsParams } from '../../../services/react-query/events';
import { Event } from '../../../types/Event';
import { UserConfigurableModule } from '../../../types/User';
import { Loading } from '../../../uikit';
import ITable from '../../../uikit/itable/ITable';
import { EventColumns } from '../../../utility/event';
import { PREFERENCES_VALID_KEYS } from '../../../types/Preference';
import { useGetAllPreferences } from '../../../services/react-query/preferences';
import PreferencePill from '../../preferences/PreferencePill';
import { FilterContext } from '../../layouts/EventsLayout/EventsLayout';
import _ from 'lodash';
import { useDownloadCsv } from '../../../hooks/useDownloadCsv';

export default function EventTable() {
  const { currentPage, setCurrentPage } = useSyncPageParams();
  const { userSorts } = useUserSorts<EventSortParam>(UserConfigurableModule.EVENTS);
  const filters = useContext(FilterContext);

  const baseParams = {
    filters,
    search: useSearchTerm(),
    sort: userSorts,
  };

  const eventQueryParams: UseGetAllEventsParams = {
    ...baseParams,
    page: currentPage,
  };

  const { data, isLoading } = useGetAllEvents({ params: eventQueryParams, enabled: true });

  const { data: prefs } = useGetAllPreferences({
    params: { filters: { keys: [PREFERENCES_VALID_KEYS.EVENT_TYPE, PREFERENCES_VALID_KEYS.EVENT_LOCATION] } },
    enabled: true,
  });

  const prefByUuid = useMemo(() => _.keyBy(prefs ?? [], 'uuid'), [prefs]);

  const pillColumns = useMemo(() => {
    return EventColumns.map((col) => {
      if (col.key === 'type_value') {
        return {
          ...col,
          render: (row: Event) => <PreferencePill {...prefByUuid[row.type_uuid ?? '']} />,
        };
      }
      if (col.key === 'location_value') {
        return {
          ...col,
          render: (row: Event) => <PreferencePill {...prefByUuid[row.location_uuid ?? '']} />,
        };
      }
      return col;
    });
  }, [prefByUuid]);

  const { isDownloadingCsv, handleDownloadCsv } = useDownloadCsv({
    params: baseParams,
    controller: 'events',
  });

  if (isLoading) return <Loading />;

  const events = data?.data || [];
  const meta = data?.meta;

  return (
    <ITable<Event, EventAvailableColumns>
      availableColumns={pillColumns}
      data={events}
      module={UserConfigurableModule.EVENTS}
      page={currentPage}
      count={meta?.total_pages ?? 1}
      isDownloadingCsv={isDownloadingCsv}
      onDownloadCsv={handleDownloadCsv}
      onPageChange={(_e, v) => setCurrentPage(v)}
    />
  );
}
